import { Invoice } from "v2/hooks/billing/useInvoices";
import { fmtCurrencyCompact } from "utils";
import { getPlanTier, isEnterprise } from "utils/planUtils";
import { BillingAddress, InvoiceTransformedLineItem, TransformedInvoice } from "../types";

/**
 * Transform invoice data from API format to component format
 */
const PLAN_TIER_DISPLAY_NAMES: Record<string, string> = {
  free: "Free",
  hobby: "Hobby",
  startup: "Startup",
  business: "Business",
  scaling: "Scaling",
};

/**
 * Map plan slugs that don't fit the PLG tier scheme to their display name.
 * `getPlanTier` falls back to "free" for unrecognised slugs, so without these explicit
 * checks enterprise / custom / Mini / trial users would see their invoices labelled "Free".
 */
function getSpecialSlugDisplayName(entityId: string): string | null {
  if (isEnterprise(entityId)) return "Enterprise";
  if (entityId === "custom") return "Custom";
  if (entityId === "student_npo") return "Student / NPO";
  // Affiliate variants are the backend slugs for Chargebee's "Mini" plans.
  if (entityId.includes("affiliate")) return "Mini";
  if (entityId === "extended_trial") return "Extended Trial";
  if (entityId === "standard_extended_trial") return "1 Week Trial";
  return null;
}

/**
 * Extract a display-friendly plan name from a Chargebee invoice.
 * Uses entity_id from the first plan line item, falling back to description parsing.
 */
function getInvoicePlanName(invoice: Invoice): string {
  // Look for a plan line item's entity_id (e.g. "professional_202503", "business_2", "free")
  const planLineItem = invoice.line_items?.find(item => item.entity_type === "plan_item_price" || item.entity_type === "plan");
  if (planLineItem?.entity_id) {
    const specialName = getSpecialSlugDisplayName(planLineItem.entity_id);
    if (specialName) return specialName;

    const tier = getPlanTier(planLineItem.entity_id);
    return PLAN_TIER_DISPLAY_NAMES[tier] || tier.charAt(0).toUpperCase() + tier.slice(1);
  }

  // Fallback: parse from description
  const description = invoice.line_items?.[0]?.description || "";
  const match = description.match(/ScraperAPI\s+(.+)/i);
  if (match) {
    const specialName = getSpecialSlugDisplayName(match[1].trim());
    if (specialName) return specialName;

    const tier = getPlanTier(match[1].trim());
    return PLAN_TIER_DISPLAY_NAMES[tier] || match[1].charAt(0).toUpperCase() + match[1].slice(1);
  }

  return "Pay As You Go";
}

export function transformInvoices(invoices?: Invoice[]): TransformedInvoice[] {
  if (!invoices || invoices.length === 0) return [];

  return invoices.map(invoice => {
    return {
      id: invoice.id,
      date: invoice.date,
      status: invoice.status,
      planName: getInvoicePlanName(invoice),
      amount: (invoice.status === "paid" ? invoice.amount_paid : invoice.amount_due) / 100,
      currencyCode: invoice.currency_code,
      invoiceNumber: invoice.id, // Use raw Chargebee ID for support clarity
      pdfDownloadUrl: invoice.pdf_download_url,
      lineItems: invoice.line_items?.map((li): InvoiceTransformedLineItem => ({
        id: li.id,
        description: li.description,
        amount: li.amount / 100,
        quantity: li.quantity,
        unitAmount: li.unit_amount ? li.unit_amount / 100 : undefined,
        entityType: li.entity_type,
      })),
      total: (invoice.total ?? 0) / 100,
    };
  });
}


/**
 * Helper to extract value from API response that may be wrapped in validation object
 * Handles three formats:
 * 1. Direct value: "John"
 * 2. Validation-wrapped: { value: "John", validation: {...} }
 * 3. Missing/null/undefined: returns empty string
 */
function extractValue(field: any): string {
  if (!field) return "";
  if (typeof field === 'string') return field;
  if (typeof field === 'object' && 'value' in field) {
    return field.value || "";
  }
  return "";
}

/**
 * Transform billing address from API format to component format
 * Handles both validation-wrapped format (from billing microservice) and direct values
 */
export function transformBillingAddress(billingAddressData?: any): BillingAddress | undefined {
  if (!billingAddressData) return undefined;

  // If it's an empty object, return undefined
  if (Object.keys(billingAddressData).length === 0) return undefined;

  const transformed = {
    firstName: extractValue(billingAddressData.first_name),
    lastName: extractValue(billingAddressData.last_name),
    company: extractValue(billingAddressData.company),
    vatNumber: extractValue(billingAddressData.vat_number),
    address: extractValue(billingAddressData.line1),
    city: extractValue(billingAddressData.city),
    state: extractValue(billingAddressData.state),
    zip: extractValue(billingAddressData.zip),
    country: extractValue(billingAddressData.country),
  };

  // If all fields are empty, return undefined (no billing address set)
  const hasAnyValue = Object.values(transformed).some(value => value !== "");
  if (!hasAnyValue) return undefined;

  return transformed;
}

// ============================================================================
// Subscription Pricing Transformations
// ============================================================================

/**
 * Format subscription price from cents to currency string
 */
export function formatSubscriptionPrice(
  priceInCents: number | undefined | null,
  currencyCode: string | undefined | null
): string | null {
  if (!priceInCents || !currencyCode) return null;

  return fmtCurrencyCompact(priceInCents / 100, currencyCode);
}

/**
 * Calculate billing period display text
 * Handles legacy case where annual plans are stored as 12 months
 */
export function calculateBillingPeriod(
  billingPeriod: number | undefined | null,
  billingPeriodUnit: string | undefined | null
): string {
  if (billingPeriod === 12 && billingPeriodUnit === "month") {
    return "year";
  }
  return billingPeriodUnit || "month";
}
